import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, IdCard, CalendarDays } from "lucide-react";
import Navbar from "@/components/Navbar";
import { GolonganBadge } from "@/components/Badges";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

export default function ProfilePage() {
  const { user, setUser } = useAuth();
  const [meta, setMeta] = useState({ golongan: [], kwarran: [], saka: [] });
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", nta: "", golongan: "", gudep: "", kwarran: "", tanggal_dilantik: "", saka: "", pramuka_garuda: false,
  });
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get("/meta").then(({ data }) => setMeta(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) setForm({
      name: user.name || "", nta: user.nta || "", golongan: user.golongan || "",
      gudep: user.gudep || "", kwarran: user.kwarran || "", tanggal_dilantik: user.tanggal_dilantik || "",
      saka: user.saka || "", pramuka_garuda: !!user.pramuka_garuda,
    });
  }, [user]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/profile", form);
      setUser(data);
      toast.success("Profil berhasil diperbarui");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full rounded-lg border border-[#E5DFD7] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#E5A93C] focus:ring-2 focus:ring-[#E5A93C]/30 transition";
  const labelCls = "block text-xs uppercase tracking-widest font-bold text-[#8C827A] mb-1.5";

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8">
          <h1 className="font-display text-3xl font-extrabold text-[#1F1917]">Profil Pramuka</h1>
          <p className="text-[#8C827A] mt-1">Lengkapi data diri Anda sebagai anggota Gerakan Pramuka Kota Depok.</p>
        </div>

        <div className="bg-gradient-to-br from-[#4A2E2B] to-[#6B3E2E] rounded-2xl p-6 text-white mb-8 flex items-center gap-5">
          <div className="h-16 w-16 rounded-2xl bg-white/15 flex items-center justify-center">
            <IdCard className="h-8 w-8 text-[#E5A93C]" />
          </div>
          <div className="flex-1">
            <div className="font-display text-xl font-extrabold">{user?.name}</div>
            <div className="text-white/70 text-sm">NTA: {user?.nta || "—"}</div>
          </div>
          <div className="text-right"><GolonganBadge value={user?.golongan} /></div>
        </div>

        <form onSubmit={save} className="bg-white rounded-xl border border-[#E5DFD7] shadow-sm p-6 sm:p-8 space-y-5">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>Nama Lengkap</label>
              <input data-testid="profile-input-nama" className={inputCls} value={form.name}
                onChange={(e) => upd("name", e.target.value)} required />
            </div>
            <div>
              <label className={labelCls}>NTA (Nomor Tanda Anggota)</label>
              <input data-testid="profile-input-nta" className={inputCls} value={form.nta}
                onChange={(e) => upd("nta", e.target.value)} required />
            </div>
            <div>
              <label className={labelCls}>Golongan</label>
              <select data-testid="profile-select-golongan" className={inputCls} value={form.golongan}
                onChange={(e) => upd("golongan", e.target.value)}>
                <option value="">Pilih Golongan</option>
                {meta.golongan.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Kwarran (Kecamatan)</label>
              <select data-testid="profile-select-kwarran" className={inputCls} value={form.kwarran}
                onChange={(e) => upd("kwarran", e.target.value)}>
                <option value="">Pilih Kwarran</option>
                {meta.kwarran.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Gugus Depan (Gudep)</label>
              <input data-testid="profile-input-gudep" className={inputCls} value={form.gudep}
                onChange={(e) => upd("gudep", e.target.value)} placeholder="cth: SMPN 1 Depok" />
            </div>
            <div>
              <label className={labelCls}><CalendarDays className="inline h-3 w-3 mr-1" />Tanggal Dilantik</label>
              <input data-testid="profile-input-tanggal-dilantik" type="date" className={inputCls} value={form.tanggal_dilantik}
                onChange={(e) => upd("tanggal_dilantik", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Satuan Karya (Saka)</label>
              <select data-testid="profile-select-saka" className={inputCls} value={form.saka}
                onChange={(e) => upd("saka", e.target.value)}>
                <option value="">Tidak bergabung Saka</option>
                {(meta.saka || []).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <label data-testid="profile-checkbox-garuda" className="flex items-center gap-3 w-full rounded-lg border border-[#E5DFD7] bg-[#F7F4EF] px-3.5 py-2.5 cursor-pointer">
                <input type="checkbox" className="h-4 w-4 accent-[#B85C38]" checked={form.pramuka_garuda}
                  onChange={(e) => upd("pramuka_garuda", e.target.checked)} />
                <span className="text-sm font-semibold text-[#4A2E2B]">Saya Pramuka Garuda</span>
              </label>
            </div>
          </div>
          <button data-testid="profile-save-btn" type="submit" disabled={saving}
            className="inline-flex items-center gap-2 bg-[#E5A93C] text-[#2A1F1B] px-6 py-3 rounded-lg font-bold hover:bg-[#d69a2b] transition disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Simpan Profil
          </button>
        </form>
      </div>
    </div>
  );
}
